import axios from "axios";
import {
  User,
  LoginData,
  RegisterData,
  Unit,
  UnitAssignment,
  TopicsAndUnitsResponse,
  Child,
  PreferredTiming,
  DashboardStats,
  StudentProgress,
  Cohort,
  TutorRating,
  MentorTutorRating,
  TutorReview,
  ProficiencyRating,
  ProjectTemplate,
  LeaderboardPayload,
  AttendanceRecord,
  StudentBillingEntry,
  Curriculum,
  Topic,
  CourseAdvertisement,
  CourseAdvertisementCreate,
  Opportunity,
  OpportunityPayload,
  OpportunityInterestEntry,
  DemoListItem,
  DemoInviteItem,
  DemoRequestItem,
  ConfirmedDemoScheduleItem,
  Registration,
  SignupPayload,
  SignupResponse,
  SignupEntry,
  RegistrationStats,
  RegistrationCoursesResponse,
  RegistrationCourseCohortsResponse,
  ScheduleEvent,
  UnitDocument,
  PendingDocumentsResponse,
  InboxThread,
  InboxMessage,
} from "../types";

// Use relative URL since we're proxying via frontend dev server
const API_BASE_URL = "/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Get CSRF token from cookies
const getCSRFToken = () => {
  // Guard against Node.js test environment where document is undefined
  if (typeof document === "undefined") {
    return null;
  }
  const name = "csrftoken";
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift();
  }
  return null;
};

// Guard flag: when true the request interceptor skips the CSRF fetch to avoid recursion
let skipCSRFInterceptor = false;

// Get CSRF token from API if not in cookies
const ensureCSRFToken = async () => {
  let csrfToken = getCSRFToken();
  if (!csrfToken && !skipCSRFInterceptor) {
    skipCSRFInterceptor = true;
    try {
      const response = await api.get("/users/csrf_token/");
      csrfToken = response.data.csrfToken;
    } catch (error) {
      console.warn("Could not get CSRF token:", error);
    } finally {
      skipCSRFInterceptor = false;
    }
  }
  return csrfToken;
};

// Add CSRF token to state-changing requests (POST, PUT, PATCH, DELETE)
api.interceptors.request.use(async (config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const csrfToken = await ensureCSRFToken();
    if (csrfToken) {
      config.headers["X-CSRFToken"] = csrfToken;
    }
  }
  return config;
});

// Session status codes returned by SessionStatusMiddleware
const SESSION_STATUS_CODES = ["SESSION_DISPLACED", "SESSION_EXPIRED"] as const;
type SessionStatusCode = (typeof SESSION_STATUS_CODES)[number];

// Callback invoked when a session is invalidated (set by AuthContext)
let onSessionInvalidated:
  ((code: SessionStatusCode, detail: string) => void) | null = null;

export const setSessionInvalidatedCallback = (
  cb: ((code: SessionStatusCode, detail: string) => void) | null,
) => {
  onSessionInvalidated = cb;
};

// Handle authentication errors and retry on CSRF/session failures
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as any;

    // Check for session displacement / expiry from SessionStatusMiddleware
    if (error.response?.status === 401) {
      const code = error.response.data?.code;
      if (SESSION_STATUS_CODES.includes(code)) {
        if (onSessionInvalidated) {
          onSessionInvalidated(code, error.response.data.detail);
        }
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      const errorDetail401 = error.response.data?.detail || "";
      const isAuthMissing401 =
        typeof errorDetail401 === "string" &&
        errorDetail401
          .toLowerCase()
          .includes("authentication credentials were not provided");
      const method401 = (originalRequest.method || "get").toLowerCase();

      // DRF SessionAuthentication can return 401 when the CSRF token is
      // missing/stale on state-changing requests.  Refresh the token and retry
      // once before giving up.
      if (
        isAuthMissing401 &&
        ["post", "put", "patch", "delete"].includes(method401)
      ) {
        originalRequest._retry = true;
        try {
          const tempApi = axios.create({
            baseURL: API_BASE_URL,
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          });
          const csrfResp = await tempApi.get("/users/csrf_token/");
          const freshToken = csrfResp.data.csrfToken || getCSRFToken();
          if (freshToken) {
            originalRequest.headers["X-CSRFToken"] = freshToken;
          }
          return api(originalRequest);
        } catch (retryError) {
          console.error(
            "CSRF token refresh on 401 failed, not retrying:",
            retryError,
          );
        }
      } else {
        console.error("Authentication error:", error.response.data);
      }
    }

    if (error.response?.status === 403 && !originalRequest._retry) {
      const errorDetail =
        error.response.data?.detail || error.response.data?.error || "";
      const isCsrfError =
        typeof errorDetail === "string" &&
        errorDetail.toLowerCase().includes("csrf");
      const isAuthMissing =
        typeof errorDetail === "string" &&
        errorDetail
          .toLowerCase()
          .includes("authentication credentials were not provided");

      // Retry once after refreshing the CSRF token for CSRF failures or
      // missing-auth errors on state-changing requests (the session cookie
      // may still be valid but the CSRF token was stale/missing).
      const method = (originalRequest.method || "get").toLowerCase();
      if (
        (isCsrfError || isAuthMissing) &&
        ["post", "put", "patch", "delete"].includes(method)
      ) {
        originalRequest._retry = true;
        try {
          // Force-fetch a fresh CSRF token from the server
          const tempApi = axios.create({
            baseURL: API_BASE_URL,
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          });
          const csrfResp = await tempApi.get("/users/csrf_token/");
          const freshToken = csrfResp.data.csrfToken || getCSRFToken();
          if (freshToken) {
            originalRequest.headers["X-CSRFToken"] = freshToken;
          }
          return api(originalRequest);
        } catch (retryError) {
          console.error("CSRF token refresh failed, not retrying:", retryError);
        }
      } else {
        console.error(
          "Authorization error - user may not have permission or session expired:",
          error.response.data,
        );
      }
    }
    return Promise.reject(error);
  },
);

// Authentication
export const authAPI = {
  register: (data: RegisterData) => api.post<User>("/users/register/", data),
  login: (data: LoginData) => api.post<User>("/users/login/", data),
  logout: () => api.post("/users/logout/"),
  getCurrentUser: () => api.get<User>("/users/me/"),
  getCSRFToken: () => api.get("/users/csrf_token/"),

  // Password Reset
  forgotPassword: (email: string) =>
    api.post("/users/forgot_password/", { email }),
  validateResetToken: (uid: string, token: string) =>
    api.get("/users/validate_reset_token/", { params: { uid, token } }),
  resetPassword: (uid: string, token: string, newPassword: string) =>
    api.post("/users/reset_password/", {
      uid,
      token,
      new_password: newPassword,
    }),

  // OAuth registration completion (for users who signed up with Google)
  completeOAuthRegistration: (data: Partial<RegisterData>) =>
    api.post<User & { requires_approval?: boolean }>(
      "/users/complete_oauth_registration/",
      data,
    ),
};

// Users
export const usersAPI = {
  getAll: () => api.get<User[]>("/users/"),
  getById: (id: number) => api.get<User>(`/users/${id}/`),
  getStudents: () => api.get<User[]>("/users/students/"),
  getParentDashboardStats: () =>
    api.get<DashboardStats>("/users/parent_dashboard_stats/"),
  getStudentDashboardStats: () =>
    api.get<DashboardStats>("/users/student_dashboard_stats/"),
  getChildrenProgress: (params?: {
    cohort_id?: number;
    student_id?: number;
    opportunities_page?: number;
  }) => {
    const search = new URLSearchParams();
    if (params?.cohort_id) search.set("cohort_id", params.cohort_id.toString());
    if (params?.student_id)
      search.set("student_id", params.student_id.toString());
    if (params?.opportunities_page)
      search.set("opportunities_page", params.opportunities_page.toString());
    const qs = search.toString() ? `?${search.toString()}` : "";
    return api.get<StudentProgress[]>(`/users/children_progress/${qs}`);
  },
  getStudentOpportunities: (page = 1, pageSize = 10, search = "") => {
    const qs = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });

    if (search.trim()) {
      qs.set("search", search.trim());
    }

    return api.get<{
      results: Opportunity[];
      available_courses?: import("../types").AvailableCourse[];
      total_count: number;
      page: number;
      page_size: number;
      total_pages: number;
    }>(`/users/student_opportunities/?${qs.toString()}`);
  },
  getPlanner: (studentId?: number) =>
    studentId != null
      ? api.get(`/users/planner/?student_id=${studentId}`)
      : api.get("/users/planner/"),
  addToPlanner: (studentId: number | undefined, opportunityId: number) =>
    studentId != null
      ? api.post("/users/planner/", {
          student_id: studentId,
          opportunity_id: opportunityId,
        })
      : api.post("/users/planner/", { opportunity_id: opportunityId }),
  removeFromPlanner: (studentId: number | undefined, opportunityId: number) =>
    studentId != null
      ? api.delete(
          `/users/planner/?student_id=${studentId}&opportunity_id=${opportunityId}`,
        )
      : api.delete(`/users/planner/?opportunity_id=${opportunityId}`),
  getScheduleEvents: (studentId?: number) =>
    studentId != null
      ? api.get<ScheduleEvent[]>(
          `/users/schedule-events/?student_id=${studentId}`,
        )
      : api.get<ScheduleEvent[]>("/users/schedule-events/"),
  requestCohortDemo: (
    studentId: number,
    cohortId: number,
    demoSlots?: Array<{ date: string; time_start: string; time_end: string }>,
  ) =>
    api.post<{
      interest_id: number;
      notification_id?: number;
      message: string;
    }>("/users/cohort_demo_interest/", {
      student_id: studentId,
      cohort_id: cohortId,
      demo_slots: demoSlots ?? [],
    }),
  setOpportunityInterest: (
    studentId: number,
    opportunityId: number,
    interestOne1: boolean,
    interestGroup: boolean,
  ) =>
    api.post("/users/opportunity_interest/", {
      student_id: studentId,
      opportunity_id: opportunityId,
      interest_one_1: interestOne1,
      interest_group: interestGroup,
    }),
  getOpportunityAdvertisements: (
    opportunityId: number,
    studentId: number,
    page = 1,
    pageSize = 5,
  ) =>
    api.get<import("../types").ParentOpportunityAdvertisementsResponse>(
      `/users/opportunity_advertisements/?opportunity_id=${opportunityId}&student_id=${studentId}&page=${page}&page_size=${pageSize}`,
    ),
  getPrepCourseOptions: (opportunityId: number, studentId: number) =>
    api.get<{
      options: Array<{
        delivery_mode: string;
        cohort_count: number;
        advertisement_count: number;
      }>;
    }>(
      `/users/prep_course_options/?opportunity_id=${opportunityId}&student_id=${studentId}`,
    ),
  getPrepCourseCourses: (
    opportunityId: number,
    studentId: number,
    deliveryMode: "online_only" | "in_person" | "hybrid",
  ) =>
    api.get<{
      courses: Array<{
        id: number;
        title: string;
        description: string;
        cohort_count: number;
      }>;
      advertisements: import("../types").ParentOpportunityAdvertisement[];
    }>(
      `/users/prep_course_courses/?opportunity_id=${opportunityId}&student_id=${studentId}&delivery_mode=${deliveryMode}`,
    ),
  getPrepCourseCohorts: (
    opportunityId: number,
    courseId: number,
    studentId: number,
    deliveryMode: "online_only" | "in_person" | "hybrid",
  ) =>
    api.get<{
      course: { id: number; title: string };
      cohorts: Array<
        import("../types").CourseCohortWithSchedules & {
          sign_up_url?: string | null;
        }
      >;
    }>(
      `/users/prep_course_cohorts/?opportunity_id=${opportunityId}&course_id=${courseId}&student_id=${studentId}&delivery_mode=${deliveryMode}`,
    ),
  getOpportunityPrepContent: (
    opportunityId: number,
    studentId: number,
    deliveryMode: "online_only" | "in_person" | "hybrid",
  ) =>
    api.get<{
      cohorts: import("../types").CourseCohortWithSchedules[];
      advertisements: import("../types").ParentOpportunityAdvertisement[];
    }>(
      `/users/opportunity_prep_content/?opportunity_id=${opportunityId}&student_id=${studentId}&delivery_mode=${deliveryMode}`,
    ),
  submitAdvertisementInterest: (
    opportunityId: number,
    studentId: number,
    selections: Array<{
      advertisement_id: number;
      interest_one_1: boolean;
      interest_group: boolean;
      preferred_days?: string[];
      preferred_slots?: Array<{
        day: string;
        time_start: string;
        time_end: string;
      }>;
      demo_slots?: Array<{
        date?: string;
        day?: string;
        time_start: string;
        time_end: string;
      }>;
    }>,
    parentTimezone?: string | null,
  ) =>
    api.post("/users/opportunity_advertisement_interest/", {
      opportunity_id: opportunityId,
      student_id: studentId,
      selections,
      parent_timezone: parentTimezone ?? undefined,
    }),
  setStudentOpportunityInterest: (
    opportunityId: number,
    interestOne1: boolean,
    interestGroup: boolean,
  ) =>
    api.post("/users/student_opportunity_interest/", {
      opportunity_id: opportunityId,
      interest_one_1: interestOne1,
      interest_group: interestGroup,
    }),
  getParentCohorts: () => api.get("/cohorts/parent_cohorts/"),
  getCurrentUser: () => api.get<User>("/users/me/"),
  updateProfile: (data: Partial<User>) =>
    api.put<User>("/users/update_profile/", data),
  changePassword: (data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }) => api.post("/users/change_password/", data),

  getDemos: (timezone?: string | null) => {
    const params = timezone ? `?timezone=${encodeURIComponent(timezone)}` : "";
    return api.get<{ results: DemoListItem[]; recent_count?: number }>(
      `/users/demos/${params}`,
    );
  },
  getDemoActivityCount: () =>
    api.get<{ count: number }>("/users/demos/activity_count/"),
  acceptDemo: (demoId: number) =>
    api.post("/users/demos/accept/", { demo_id: demoId }),
  getMyDemoInvites: (timezone?: string | null) => {
    const params = timezone ? `?timezone=${encodeURIComponent(timezone)}` : "";
    return api.get<{ results: DemoInviteItem[] }>(
      `/users/demos/my_invites/${params}`,
    );
  },
  getMyDemoRequests: (timezone?: string | null) => {
    const params = timezone ? `?timezone=${encodeURIComponent(timezone)}` : "";
    return api.get<{ results: DemoRequestItem[] }>(
      `/users/demos/my_requests/${params}`,
    );
  },
  cancelDemo: (demoId: number) =>
    api.post("/users/demos/cancel/", { demo_id: demoId }),
  acceptCohortDemo: (interestId: number, demoDate: string) =>
    api.post("/users/cohort_demo_accept/", {
      interest_id: interestId,
      demo_date: demoDate,
    }),
  cancelCohortDemo: (interestId: number) =>
    api.post("/users/cohort_demo_cancel/", { interest_id: interestId }),
  updateDemoMeetingUrl: (demoId: number, meetingUrl: string) =>
    api.post("/users/demos/update_meeting_url/", {
      demo_id: demoId,
      meeting_url: meetingUrl,
    }),
  closeDemo: (demoId: number, postMortem?: string) =>
    api.post("/users/demos/close/", {
      demo_id: demoId,
      post_mortem: postMortem || "",
    }),
  createCohortFromDemo: (data: {
    demo_id: number;
    name: string;
    description: string;
    main_challenge_category?: number | null;
    template?: number | null;
  }) =>
    api.post<{ message: string; cohort_id: number; cohort_name: string }>(
      "/users/demos/create_cohort_from_demo/",
      data,
    ),
  getConfirmedDemoSchedule: (timezone?: string | null) => {
    const params = timezone ? `?timezone=${encodeURIComponent(timezone)}` : "";
    return api.get<{ results: ConfirmedDemoScheduleItem[] }>(
      `/users/demos/confirmed_schedule/${params}`,
    );
  },
  getTutorDemoSchedule: () =>
    api.get<{ results: import("../types").TutorScheduleDemoItem[] }>(
      "/users/demos/tutor_schedule/",
    ),
  parentAcceptDemo: (demoId: number) =>
    api.post("/users/demos/parent_accept/", { demo_id: demoId }),
  submitDemoFeedback: (demoId: number, rating: number, feedbackText: string) =>
    api.post("/users/demos/submit_feedback/", {
      demo_id: demoId,
      rating,
      feedback_text: feedbackText,
    }),

  // Mentor approval status

  // Student approval status
  getStudentApprovalStatus: () =>
    api.get<StudentApproval>("/student-approvals/get_student_status/"),

  // Cohort leaderboard
  getCohortLeaderboard: (cohortId: number) =>
    api.get<LeaderboardPayload>(
      `/cohorts/cohort_leaderboard/?cohort_id=${cohortId}`,
    ),
  getCohortStudents: (cohortId?: number) => {
    const url = cohortId
      ? `/cohorts/cohort_students/?cohort_id=${cohortId}`
      : "/cohorts/cohort_students/";
    return api.get<User[]>(url);
  },
  getChildAttendance: (childId: number, cohortId?: number) => {
    const params = new URLSearchParams({ child_id: childId.toString() });
    if (cohortId) {
      params.append("cohort_id", cohortId.toString());
    }
    return api.get<AttendanceRecord[]>(
      `/users/child_attendance/?${params.toString()}`,
    );
  },

  /** Same shape as student topics_and_units; lists only incomplete unit work and documents */
  getParentTopicsAndUnits: (studentId: number, cohortId: number) =>
    api.get<import("../types").TopicsAndUnitsResponse>(
      `/cohorts/parent_topics_and_units/?student_id=${studentId}&cohort_id=${cohortId}`,
    ),
  getParentStudentAssignments: (studentId: number, cohortId: number) =>
    api.get<import("../types").ParentTopicGroup[]>(
      `/cohorts/parent_student_assignments/?student_id=${studentId}&cohort_id=${cohortId}`,
    ),
  getParentGradedSubmissions: (
    studentId: number,
    cohortId: number,
    limit = 50,
  ) =>
    api.get<import("../types").ParentGradedSubmission[]>(
      `/cohorts/parent_graded_submissions/?student_id=${studentId}&cohort_id=${cohortId}&limit=${limit}`,
    ),

  // User management
  getUserManagement: (filters?: {
    user_type?: string;
    approval_status?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.user_type) params.append("user_type", filters.user_type);
    if (filters?.approval_status)
      params.append("approval_status", filters.approval_status);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.page_size)
      params.append("page_size", filters.page_size.toString());
    return api.get(`/users/user_management/?${params.toString()}`);
  },

  // Generic methods for additional endpoints
  get: (url: string) => api.get(url),
  post: (url: string, data?: any) => api.post(url, data),
  put: (url: string, data?: any) => api.put(url, data),
  delete: (url: string) => api.delete(url),

  // Inbox (Universal Inbox)
  getInboxThreads: () => api.get<InboxThread[]>("/messages/threads/"),

  findExistingThread: (participantId: number) =>
    api.get<InboxThread>("/messages/threads/find/", {
      params: { participant: participantId },
    }),

  getInboxThread: (id: number) =>
    api.get<InboxThread>(`/messages/threads/${id}/`),

  createInboxThread: (data: {
    recipient_ids: number[];
    subject: string;
    body: string;
  }) => api.post<InboxThread>("/messages/threads/", data),

  replyToInboxThread: (threadId: number, body: string) =>
    api.post<InboxMessage>(`/messages/threads/${threadId}/messages/`, { body }),

  getInboxUnreadCount: () =>
    api.get<{ unread: number }>("/messages/unread-count/"),

  /** Backend may return a DRF paginated object or a plain list */
  searchUsers: (query: string) =>
    api.get<
      | User[]
      | {
          count?: number;
          next?: string | null;
          previous?: string | null;
          results: User[];
        }
    >("/users/", { params: { search: query, page_size: 20 } }),
};

// Attendance
type AttendanceCreatePayload = {
  cohort: number;
  session_date: string;
  start_time: string;
  duration_minutes: number;
  attendees?: number[];
  topic?: string;
  notes?: string;
};

export const attendanceAPI = {
  list: (params?: {
    cohort?: number;
    start_date?: string;
    end_date?: string;
  }) => api.get<AttendanceRecord[]>("/attendance-records/", { params }),
  create: (data: AttendanceCreatePayload) =>
    api.post<AttendanceRecord>("/attendance-records/", data),
  update: (id: number, data: Partial<AttendanceCreatePayload>) =>
    api.patch<AttendanceRecord>(`/attendance-records/${id}/`, data),
  delete: (id: number) => api.delete(`/attendance-records/${id}/`),
};

// Opportunities
export const opportunitiesAPI = {
  list: (params?: Record<string, string | number | boolean>) =>
    api.get<Opportunity[]>("/opportunities/", { params }),
  getForAdvertisement: (search?: string) =>
    api.get<Opportunity[]>("/opportunities/for-advertisement/", {
      params: search ? { search } : undefined,
    }),
  create: (data: OpportunityPayload) =>
    api.post<Opportunity>("/opportunities/", data),
  update: (id: number, data: OpportunityPayload) =>
    api.put<Opportunity>(`/opportunities/${id}/`, data),
  delete: (id: number) => api.delete(`/opportunities/${id}/`),
  approve: (id: number) =>
    api.post<Opportunity>(`/opportunities/${id}/approve/`),
  unapprove: (id: number) =>
    api.post<Opportunity>(`/opportunities/${id}/unapprove/`),
  incrementInterest: (id: number) =>
    api.post<Opportunity>(`/opportunities/${id}/increment-interest/`),
};

// Forums
export interface ForumTopic {
  id: number;
  slug: string;
  title: string;
  description: string;
  type: string | null;
  category: string | null;
  grade_levels: string[];
  event_date: string | null;
  event_dates: string[];
  registration_deadline: string | null;
  comment_count: number;
}

export interface ForumTopicListResponse {
  topics: ForumTopic[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  tag_counts: {
    grade: Record<string, number>;
    category: Record<string, number>;
    type: Record<string, number>;
  };
}

export interface ForumComment {
  id: number;
  author_username: string;
  author_first_name: string;
  author_opportunity_points: number;
  body: string;
  created_at: string;
}

export interface ForumTopicDetail {
  id: number;
  slug: string;
  title: string;
  description: string;
  event_date: string | null;
  event_dates: string[];
  registration_deadline: string | null;
  comments: ForumComment[];
}

export const forumsAPI = {
  listTopics: (params?: {
    page?: number;
    page_size?: number;
    search?: string;
    grade?: string;
    category?: string;
    type?: string;
  }) => api.get<ForumTopicListResponse>("/forums/topics/", { params }),
  getTopic: (slug: string) =>
    api.get<ForumTopicDetail>(`/forums/topics/${slug}/`),
  addComment: (slug: string, body: string) =>
    api.post<ForumComment>(`/forums/topics/${slug}/comments/`, { body }),
};

// Feature Requests
export interface FeatureRequest {
  id: number;
  user: number;
  user_username: string;
  user_email: string;
  message: string;
  status: "pending" | "approved" | "rejected";
  reviewed_by: number | null;
  reviewed_at: string | null;
  created_at: string;
}

export const featureRequestsAPI = {
  list: () => api.get<FeatureRequest[]>("/feature-requests/"),
  create: (message: string) =>
    api.post<FeatureRequest>("/feature-requests/", { message }),
  approve: (id: number) =>
    api.post<{ status: string }>(`/feature-requests/${id}/approve/`),
  reject: (id: number) =>
    api.post<{ status: string }>(`/feature-requests/${id}/reject/`),
};

// Topics (challenges in API)
export const challengesAPI = {
  getAll: (mainCategoryId?: number) => {
    const params = new URLSearchParams();
    if (mainCategoryId)
      params.append("main_category", mainCategoryId.toString());
    const query = params.toString();
    return api.get<Unit[]>(`/unit-categories/${query ? "?" + query : ""}`);
  },
  getByCohort: (cohortId: number) =>
    api.get<Unit[]>(`/unit-categories/by_cohort/?cohort_id=${cohortId}`),
  getById: (id: number) => api.get<Unit>(`/unit-categories/${id}/`),
  create: (data: Partial<Unit>) => api.post<Unit>("/unit-categories/", data),
  update: (id: number, data: Partial<Unit>) =>
    api.patch<Unit>(`/unit-categories/${id}/`, data),
  delete: (id: number) => api.delete(`/unit-categories/${id}/`),
  getBigChallenges: () => api.get<Unit[]>("/unit-categories/big_challenges/"),
  getChildChallenges: (parentId: number) =>
    api.get<Unit[]>(
      `/unit-categories/child_challenges/?parent_challenge=${parentId}`,
    ),
  addChildChallenge: (parentId: number, data: Partial<Unit>) =>
    api.post(`/unit-categories/${parentId}/add_child_challenge/`, data),
  reorderChildren: (parentId: number, childOrders: any[]) =>
    api.post(`/unit-categories/${parentId}/reorder_children/`, {
      child_orders: childOrders,
    }),
  getCategories: () => api.get("/unit-categories/categories/"),
  getMyChallenges: () => api.get<Unit[]>("/unit-categories/my_challenges/"),
  getStatistics: () => api.get("/unit-categories/statistics/"),
  // Document upload
  requestUpload: (
    unitId: number,
    data: {
      file_name: string;
      content_type: string;
      file_size: number;
      /** Replace pending/rejected document (same unit slot). */
      replace_document_id?: number;
    },
  ) =>
    api.post<{ url: string; fields: Record<string, string>; file_key: string }>(
      `/unit-categories/${unitId}/request_upload/`,
      data,
    ),
  confirmUpload: (
    unitId: number,
    data: {
      file_key: string;
      file_name: string;
      file_size: number;
      content_type: string;
      /**
       * Defaults to read_only. `staff_only` (issue #1112) is admin-only — the
       * server rejects it from non-admin uploaders.
       */
      document_type?: "read_only" | "questionnaire" | "quiz" | "staff_only";
      replace_document_id?: number;
      /** User-visible label (`display_name`). Omit or send blank to use `file_name`. */
      display_name?: string;
    },
  ) => api.post(`/unit-categories/${unitId}/confirm_upload/`, data),
  patchDocument: (
    docId: number,
    data: {
      document_type?: "read_only" | "questionnaire" | "quiz" | "staff_only";
      quiz_definition?: Record<string, unknown>;
      /** Editable only while document is pending_approval or rejected. */
      display_name?: string;
    },
  ) => api.patch<UnitDocument>(`/unit-categories/documents/${docId}/`, data),
  /** Practice scoring for staff — does not write student assignment rows. */
  previewQuiz: (docId: number, answers: Record<string, string>) =>
    api.post<{
      percent: number;
      correct: number;
      total: number;
      passed: boolean;
      pass_threshold_percent: number;
    }>(`/unit-categories/documents/${docId}/preview_quiz/`, { answers }),
  getDocuments: (unitId: number) =>
    api.get<UnitDocument[]>(`/unit-categories/${unitId}/documents/`),
  getDocumentDownloadUrl: (docId: number) =>
    api.get<{
      url: string;
      file_name: string;
      disposition?: string;
      // False when the doc is staff_only and the requester is tutor/mentor
      // (issue #1112): preview is allowed but explicit "Download" UI must be hidden.
      download_allowed?: boolean;
    }>(`/unit-categories/documents/${docId}/download/`),
  deleteDocument: (docId: number) =>
    api.delete(`/unit-categories/documents/${docId}/`),
  approveDocument: (docId: number) =>
    api.post<UnitDocument>(`/unit-categories/documents/${docId}/approve/`),
  rejectDocument: (docId: number, rejectionReason: string) =>
    api.post<UnitDocument>(`/unit-categories/documents/${docId}/reject/`, {
      rejection_reason: rejectionReason,
    }),
  getPendingDocuments: () =>
    api.get<PendingDocumentsResponse>("/unit-categories/documents/pending/"),
  /**
   * Pending questionnaire approval: resolve cohort + template from the unit’s curriculum
   * (course that includes that curriculum → cohort). No cohort picker.
   */
  getTryRespondTemplateForDocument: (docId: number) =>
    api.get<{
      cohort_id: number;
      cohort_name: string;
      template: ProjectTemplate;
    }>(`/unit-categories/documents/${docId}/try_respond_template/`),
};

// Curriculum (main challenge categories)
export const challengeCategoriesAPI = {
  // Main categories
  getMainCategories: () => api.get<Curriculum[]>("/curriculum-categories/"),
  getMainCategory: (id: number) =>
    api.get<Curriculum>(`/curriculum-categories/${id}/`),
  createMainCategory: (data: {
    name: string;
    description?: string;
    slug?: string;
  }) => api.post<Curriculum>("/curriculum-categories/", data),
  updateMainCategory: (id: number, data: Partial<Curriculum>) =>
    api.put<Curriculum>(`/curriculum-categories/${id}/`, data),
  deleteMainCategory: (id: number) =>
    api.delete(`/curriculum-categories/${id}/`),

  // Topics (sub-category = topic)
  getSubCategories: (mainCategoryId?: number) => {
    const url = mainCategoryId
      ? `/topic-categories/?main_category=${mainCategoryId}`
      : "/topic-categories/";
    return api.get<Topic[]>(url);
  },
  getSubCategory: (id: number) => api.get<Topic>(`/topic-categories/${id}/`),
  createSubCategory: (data: {
    name: string;
    main_category: number;
    description?: string;
    slug?: string;
  }) => api.post<Topic>("/topic-categories/", data),
  updateSubCategory: (id: number, data: Partial<Topic>) =>
    api.put<Topic>(`/topic-categories/${id}/`, data),
  deleteSubCategory: (id: number) => api.delete(`/topic-categories/${id}/`),

  bulkCreateSubCategories: (data: {
    main_category: number;
    rows: {
      topic_name: string;
      topic_description: string;
      unit_name: string;
      unit_description: string;
    }[];
  }) =>
    api.post<{
      topics_created: number;
      units_created: number;
      error_count: number;
      results: {
        line: number;
        status: string;
        topic_name: string;
        unit_name: string;
        topic_id?: number;
        unit_id?: number;
        error?: string;
      }[];
    }>("/topic-categories/bulk-create/", data),

  /** Admin: nested JSON → curriculum + Unit topics/units (same as bulk-create). */
  importCurriculumFromJson: (data: Record<string, unknown>) =>
    api.post<{
      curriculum: Curriculum;
      topics_created: number;
      units_created: number;
      error_count: number;
      results: Array<{
        line?: number;
        status: string;
        topic_name?: string;
        unit_name?: string;
        topic_id?: number;
        unit_id?: number;
        error?: string;
      }>;
    }>("/curriculum-categories/import-json/", data),

  /**
   * Admin: bulk-upload an archive (.tar / .tar.gz / .tgz / .zip) shaped as
   * `<curriculum>/<topic>/<unit>/<file>.{pdf,png,jpg,jpeg}`. Merges into an
   * existing curriculum; same-name documents on a unit are skipped.
   */
  uploadCurriculumTarball: (
    tarball: File,
    documentType: "read_only" | "staff_only",
  ) => {
    const form = new FormData();
    form.append("tarball", tarball);
    form.append("document_type", documentType);
    return api.post<{
      curriculum_created: boolean;
      topics_created: number;
      units_created: number;
      documents_created: number;
      documents_skipped: number;
      units_skipped: number;
      warnings: string[];
    }>("/curriculum-categories/upload_tarball/", form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // Get topics for a Curriculum (sub-category = topic)
  getSubCategoriesForMain: (mainCategoryId: number) =>
    api.get<Topic[]>(
      `/curriculum-categories/${mainCategoryId}/sub_categories/`,
    ),
};

// Student Challenges (`/curriculum-assignments/` — list UnitAssignment rows for legacy screens; provisioning uses `assignmentDocumentProvisioningAPI`)
export const studentChallengesAPI = {
  getAll: () => api.get<UnitAssignment[]>("/curriculum-assignments/"),
  getMyChallenges: () =>
    api.get<UnitAssignment[]>("/curriculum-assignments/my_challenges/"),
  getById: (id: number) =>
    api.get<UnitAssignment>(`/curriculum-assignments/${id}/`),
  create: (data: Partial<UnitAssignment>) =>
    api.post<UnitAssignment>("/curriculum-assignments/", data),
  update: (id: number, data: Partial<UnitAssignment>) =>
    api.patch<UnitAssignment>(`/curriculum-assignments/${id}/`, data),
  delete: (id: number) => api.delete(`/curriculum-assignments/${id}/`),
  getGradedByCohort: (cohortId: number, limit = 50) =>
    api.get<any[]>(
      `/curriculum-assignments/graded_by_cohort/?cohort_id=${cohortId}&limit=${limit}`,
    ),
  getCurrentForCohort: (cohortId: number) =>
    api.get<any>(
      `/curriculum-assignments/current_for_cohort/?cohort_id=${cohortId}`,
    ),
  getTopicsAndUnits: (cohortId: number) =>
    api.get<TopicsAndUnitsResponse>(
      `/curriculum-assignments/topics_and_units/?cohort_id=${cohortId}`,
    ),
  getByStudent: (studentId: number) =>
    api.get<UnitAssignment[]>(`/curriculum-assignments/?student=${studentId}`),
};

/**
 * Per–unit-document actions (DESIGN_DOCUMENT §4.14). Provisioning (apply-documents/remove-documents)
 * and student/grader document workflows use `/unit-document-assignments/`.
 * Student questionnaire/quiz submission and tutor grading must use these endpoints —
 * not `/curriculum-assignments/{id}/submit/` or `/grade/` (410).
 */
export const documentAssignmentsAPI = {
  list: (params?: Record<string, string | number | boolean | undefined>) =>
    api.get("/unit-document-assignments/", { params }),
  get: (id: number) => api.get(`/unit-document-assignments/${id}/`),
  markRead: (id: number) =>
    api.post(`/unit-document-assignments/${id}/mark_read/`),
  submitQuestionnaire: (
    id: number,
    template_responses: Record<string, unknown>,
  ) =>
    api.post(`/unit-document-assignments/${id}/submit_questionnaire/`, {
      template_responses,
    }),
  submitQuiz: (id: number, answers: Record<string, unknown>) =>
    api.post(`/unit-document-assignments/${id}/submit_quiz/`, { answers }),
  startQuizTimer: (id: number) =>
    api.post(`/unit-document-assignments/${id}/start-quiz-timer/`),
  grade: (id: number, data: Record<string, unknown>) =>
    api.post(`/unit-document-assignments/${id}/grade/`, data),
  markComplete: (id: number) =>
    api.post(`/unit-document-assignments/${id}/mark_complete/`),
};

/** Canonical assignment provisioning: document pairs only (`unit_id` + `document_id`). */
export const assignmentDocumentProvisioningAPI = {
  applyDocuments: (data: {
    cohort_id: number;
    pairs: Array<{ unit_id: number; document_id: number }>;
    student_id?: number;
    grade?: string;
    due_date?: string;
  }) =>
    api.post<{
      created: number;
      skipped: number;
      unit_assignments_created: number;
    }>("/unit-document-assignments/apply-documents/", data),

  removeDocuments: (data: {
    cohort_id: number;
    pairs: Array<{ unit_id: number; document_id: number }>;
    student_id?: number;
  }) =>
    api.post<{ removed: number; skipped: number }>(
      "/unit-document-assignments/remove-documents/",
      data,
    ),
};

// Children Management (for parents)
export const childrenAPI = {
  getAll: () => api.get<Child[]>("/users/children/"),
  create: (data: Partial<Child>) => api.post<Child>("/users/children/", data),
  update: (id: number, data: Partial<Child>) =>
    api.patch<Child>(`/users/children/${id}/`, data),
  delete: (id: number) => api.delete(`/users/children/${id}/`),
};

// Preferred Timings
export const timingsAPI = {
  create: (childId: number, data: Partial<PreferredTiming>) =>
    api.post<PreferredTiming>(
      `/users/children/${childId}/preferred_timings/`,
      data,
    ),
  update: (childId: number, timingId: number, data: Partial<PreferredTiming>) =>
    api.put<PreferredTiming>(
      `/users/children/${childId}/preferred_timings/${timingId}/`,
      data,
    ),
  delete: (childId: number, timingId: number) =>
    api.delete(`/users/children/${childId}/preferred_timings/${timingId}/`),
};

// Cohorts
export const cohortsAPI = {
  getMyCohorts: () => api.get<Cohort[]>("/cohorts/my_cohorts/"),
  getAll: () => api.get<Cohort[]>("/cohorts/"),
  getById: (id: number) => api.get<Cohort>(`/cohorts/${id}/`),
  create: (data: Partial<Cohort>) => api.post<Cohort>("/cohorts/", data),
  update: (id: number, data: Partial<Cohort>) =>
    api.put<Cohort>(`/cohorts/${id}/`, data),
  partialUpdate: (id: number, data: Partial<Cohort>) =>
    api.patch<Cohort>(`/cohorts/${id}/`, data),
  delete: (id: number) => api.delete(`/cohorts/${id}/`),
  // Manager portal endpoints
  getManagerCohorts: () => api.get<Cohort[]>("/cohorts/manager_cohorts/"),
  getManagerTutorSchedules: () =>
    api.get<ManagerTutorScheduleResponse[]>(
      "/cohorts/manager_tutor_schedules/",
    ),
  getManagerNotifications: () =>
    api.get<ManagerNotificationsResponse>("/cohorts/manager_notifications/"),
  getManagerDues: () =>
    api.get<{
      dues_data: StudentBillingEntry[];
      total_students_with_dues: number;
      default_rate_per_credit_usd: number;
    }>("/users/manager_dues/"),
};

/**
 * Intern-only API namespace (Phase 5 / issue #1153).
 *
 * Interns can read the cohorts they have been assigned to (admin-managed via
 * Cohort.interns M2M added in Phase 4), but never see the student roster on
 * those cohorts. The backend serializer strips ``students`` for intern
 * requesters; the per-cohort response shape mirrors the regular cohort detail
 * payload otherwise.
 */
export const internAPI = {
  getMyCohorts: () => api.get<Cohort[]>("/cohorts/my_intern_cohorts/"),
};

// Courses (Available Courses on dashboard - admin CRUD)
export const coursesAPI = {
  getAll: () => api.get<import("../types").Course[]>("/courses/"),
  getById: (id: number) =>
    api.get<import("../types").Course>(`/courses/${id}/`),
  getCohortsWithSchedules: (courseId: number) =>
    api.get<{ cohorts: import("../types").CourseCohortWithSchedules[] }>(
      `/courses/${courseId}/cohorts-with-schedules/`,
    ),
  create: (data: import("../types").CoursePayload) =>
    api.post<import("../types").Course>("/courses/", data),
  update: (id: number, data: Partial<import("../types").CoursePayload>) =>
    api.put<import("../types").Course>(`/courses/${id}/`, data),
  partialUpdate: (
    id: number,
    data: Partial<import("../types").CoursePayload>,
  ) => api.patch<import("../types").Course>(`/courses/${id}/`, data),
  delete: (id: number) => api.delete(`/courses/${id}/`),
  clone: (id: number) =>
    api.post<import("../types").Course>(`/courses/${id}/clone/`),
};

// Public Courses (no auth required)
export const publicCoursesAPI = {
  list: (
    params: {
      page?: number;
      search?: string;
      view?: import("../types").PublicCoursesView;
      tag?: string;
    } = {},
  ) => {
    const query: Record<string, string | number> = {};
    if (params.page && params.page > 1) query.page = params.page;
    if (params.search && params.search.trim())
      query.search = params.search.trim();
    if (params.view) query.view = params.view;
    if (params.tag) query.tag = params.tag;
    return api.get<import("../types").PublicCoursesResponse>(
      "/public-courses/",
      { params: query },
    );
  },
  retrieve: (idOrSlug: number | string) =>
    api.get<import("../types").PublicCourse>(
      `/public-courses/${encodeURIComponent(String(idOrSlug))}/`,
    ),
};

// Public Opportunities browse (no auth required)
export const publicOpportunitiesAPI = {
  list: (
    params: {
      page?: number;
      search?: string;
      grade?: import("../types").OpportunityGradeBand;
      category?: import("../types").OpportunityCategory;
      type?: import("../types").PublicOpportunityType;
    } = {},
  ) => {
    const query: Record<string, string | number> = {};
    if (params.page && params.page > 1) query.page = params.page;
    if (params.search && params.search.trim())
      query.search = params.search.trim();
    if (params.grade) query.grade = params.grade;
    if (params.category) query.category = params.category;
    if (params.type) query.type = params.type;
    return api.get<import("../types").PublicOpportunitiesResponse>(
      "/opportunities/public-list/",
      { params: query },
    );
  },
};

// Tutor Ratings
export const tutorRatingsAPI = {
  rateTutor: (data: {
    tutor: number;
    cohort: number;
    rating: number;
    feedback?: string;
  }) => api.post<TutorRating>("/tutor-ratings/rate_tutor/", data),
  getMyRatings: () => api.get<TutorRating[]>("/tutor-ratings/"),
  getAll: () => api.get<TutorRating[]>("/tutor-ratings/"),
  getById: (id: number) => api.get<TutorRating>(`/tutor-ratings/${id}/`),
  update: (id: number, data: Partial<TutorRating>) =>
    api.put<TutorRating>(`/tutor-ratings/${id}/`, data),
  delete: (id: number) => api.delete(`/tutor-ratings/${id}/`),
};

// Tutor Reviews
export const tutorReviewsAPI = {
  getAll: () => api.get<TutorReview[]>("/tutor-reviews/"),
  getById: (id: number) => api.get<TutorReview>(`/tutor-reviews/${id}/`),
  create: (
    data: Partial<TutorReview> & { mark_assignment_complete?: boolean },
  ) => api.post<TutorReview>("/tutor-reviews/", data),
  update: (id: number, data: Partial<TutorReview>) =>
    api.put<TutorReview>(`/tutor-reviews/${id}/`, data),
  patch: (
    id: number,
    data: Partial<TutorReview> & { mark_assignment_complete?: boolean },
  ) => api.patch<TutorReview>(`/tutor-reviews/${id}/`, data),
  delete: (id: number) => api.delete(`/tutor-reviews/${id}/`),
  myReviews: () => api.get<TutorReview[]>("/tutor-reviews/my_reviews/"),
  studentReviews: () =>
    api.get<TutorReview[]>("/tutor-reviews/student_reviews/"),
  /** Finalize quiz/questionnaire DA after a review was saved without mark_assignment_complete. */
  completeAssignment: (id: number) =>
    api.post(`/tutor-reviews/${id}/complete_assignment/`),
  submitReview: (id: number) => api.post(`/tutor-reviews/${id}/submit_review/`),
};

// Submission Statistics
export const submissionStatsAPI = {
  tutorStats: () => api.get("/submission-stats/tutor_stats/"),
  studentStats: () => api.get("/submission-stats/student_stats/"),
};

// Proficiency Ratings
export interface TutorCohortStudentRating {
  student_id: number;
  student_name: string;
  cohort_id: number;
  cohort_name: string;
  latest_rating: {
    id: number;
    week_start_date: string;
    overall_rating: number;
    rated_at: string;
  } | null;
  has_rating_this_week: boolean;
}

export interface TutorCohortStudentRatingsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: TutorCohortStudentRating[];
}

export const proficiencyRatingsAPI = {
  getAll: () => api.get<ProficiencyRating[]>("/proficiency-ratings/"),
  getById: (id: number) =>
    api.get<ProficiencyRating>(`/proficiency-ratings/${id}/`),
  create: (data: Partial<ProficiencyRating>) =>
    api.post<ProficiencyRating>("/proficiency-ratings/", data),
  update: (id: number, data: Partial<ProficiencyRating>) =>
    api.put<ProficiencyRating>(`/proficiency-ratings/${id}/`, data),
  delete: (id: number) => api.delete(`/proficiency-ratings/${id}/`),
  getMyCohortStudents: () =>
    api.get<{ students: User[]; cohorts: Array<{ id: number; name: string }> }>(
      "/proficiency-ratings/my_cohort_students/",
    ),
  getTutorCohortStudentRatings: (
    page: number = 1,
    pageSize: number = 20,
    cohortId?: number,
    studentName?: string,
  ) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (cohortId) params.append("cohort_id", cohortId.toString());
    if (studentName) params.append("student_name", studentName);
    return api.get<TutorCohortStudentRatingsResponse>(
      `/proficiency-ratings/tutor_cohort_student_ratings/?${params.toString()}`,
    );
  },
  getStudentRatings: (studentId: number, cohortId?: number) => {
    const params = cohortId
      ? `student_id=${studentId}&cohort_id=${cohortId}`
      : `student_id=${studentId}`;
    return api.get<ProficiencyRating[]>(
      `/proficiency-ratings/student_ratings/?${params}`,
    );
  },
  getCohortTemplate: (cohortId: number) =>
    api.get<{
      cohort_id: number;
      cohort_name: string;
      template: ProjectTemplate;
    }>(`/proficiency-ratings/cohort_template/?cohort_id=${cohortId}`),
};

// Administrator API
// Student Approval Interfaces
export interface StudentApproval {
  id: number;
  student: number;
  student_name: string;
  student_email: string;
  student_bio: string;
  status: "pending" | "approved" | "rejected";
  approved_by: number | null;
  approved_by_name: string | null;
  approval_notes: string | null;
  rejection_reason: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UnapprovedStudent {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  bio: string;
  created_at: string;
}

export interface StudentApprovalStats {
  total_students: number;
  pending_approvals: number;
  subscribed_students: number;
  approved_students: number;
  rejected_students: number;
  approval_rate: number;
}

export interface UnapprovedStudentsResponse {
  unapproved_students: UnapprovedStudent[];
}

// Parent Approval Interfaces
export interface ParentApproval {
  id: number;
  parent: number;
  parent_name: string;
  parent_username: string;
  parent_email: string;
  status: "pending" | "approved" | "rejected";
  approved_by: number | null;
  approved_by_name: string | null;
  approval_notes: string | null;
  rejection_reason: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UnapprovedParent {
  id: number;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  bio: string;
  created_at: string;
}

export interface ParentApprovalStats {
  total_parents: number;
  pending_parents: number;
  approved_parents: number;
  rejected_parents: number;
}

export interface UnapprovedParentsResponse {
  unapproved_parents: UnapprovedParent[];
}

export interface ManagerApproval {
  id: number;
  manager: number;
  manager_name: string;
  manager_email: string;
  manager_bio: string;
  status: "pending" | "approved" | "rejected";
  approved_by: number | null;
  approved_by_name: string | null;
  approval_notes: string | null;
  rejection_reason: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ManagerApprovalStats {
  total_pending: number;
  total_approved: number;
  total_rejected: number;
  total_managers: number;
}

export interface UnapprovedManagersResponse {
  managers_without_approval: Array<{
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    bio: string;
    created_at: string;
  }>;
  pending_approvals: ManagerApproval[];
}

export interface ApprovedManagersResponse {
  managers: ManagerApproval[];
}

export interface ManagerNotificationItem {
  tutor: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone_no: string;
    username: string;
  };
  cohort: {
    id: number;
    name: string;
  };
  schedule: {
    id: number;
    day_of_week: string;
    start_time: string;
    timezone: string;
    session_duration: number;
  };
  occurrence_datetime: string;
  time_until: string;
}

export interface ManagerNotificationsResponse {
  one_day_ahead: ManagerNotificationItem[];
  one_hour_ahead: ManagerNotificationItem[];
}

export interface ManagerTutorScheduleResponse {
  tutor: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    username: string;
  };
  cohorts: Array<{
    cohort: {
      id: number;
      name: string;
      description: string;
    };
    schedules: Array<{
      id: number;
      day_of_week: string;
      start_time: string;
      session_duration: number;
      start_date: string;
      end_date: string | null;
      repeat_type: string;
    }>;
  }>;
}

// Registration Code Interfaces
export interface RegistrationCode {
  id: number;
  code_type: "tutor" | "mentor" | "manager" | "intern";
  code: string;
  is_active: boolean;
  week_start: string;
  week_end: string;
  created_by: number;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  is_current_week: boolean;
  is_expired: boolean;
}

export interface RegistrationCodeCreate {
  code_type: "tutor" | "mentor" | "manager" | "intern";
  week_start: string;
  week_end: string;
}

export interface RegistrationCodeValidation {
  code: string;
  code_type: "tutor" | "mentor" | "manager" | "intern";
}

export interface ApprovedStudentsResponse {
  approved_students: UnapprovedStudent[];
}

// User Management Interfaces
export interface ChildData {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type: "student" | "tutor" | "mentor" | "parent" | "manager" | "admin";
  is_active: boolean;
  date_joined: string;
  last_login: string | null;
  approval_status:
    "pending" | "approved" | "rejected" | "no_record" | "not_required";
  subscription_status: {
    plan: "basic" | "premium" | "enterprise";
    status: "active" | "inactive" | "expired" | "cancelled" | "pending";
    is_active: boolean;
    days_remaining: number | null;
  } | null;
}

export interface UserManagementData {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  user_type:
    "student" | "tutor" | "mentor" | "parent" | "manager" | "admin" | "intern";
  bio: string;
  is_active: boolean;
  is_disabled: boolean;
  date_joined: string;
  last_login: string | null;
  parent_id: number | null;
  parent_username: string | null;
  approval_status:
    "pending" | "approved" | "rejected" | "no_record" | "not_required";
  approval_id: number | null;
  approval_notes: string | null;
  rejection_reason: string | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  children: ChildData[];
  cohorts: Array<{ id: number; name: string }>;
  opportunity_points?: number;
  subscription: {
    id: number;
    plan: "basic" | "premium" | "enterprise";
    status: "active" | "inactive" | "expired" | "cancelled" | "pending";
    start_date: string | null;
    end_date: string | null;
    auto_renew: boolean;
    is_active: boolean;
    days_remaining: number | null;
  } | null;
}

export interface UserManagementResponse {
  users: UserManagementData[];
  total_count: number;
  pagination?: {
    current_page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
    next_page: number | null;
    previous_page: number | null;
  };
  filters: {
    user_type?: string;
    approval_status?: string;
    search?: string;
  };
}

export const adminAPI = {
  // Student Approval Management
  getUnapprovedStudents: () =>
    api.get<UnapprovedStudentsResponse>(
      "/student-approvals/unapproved_students/",
    ),
  getPendingStudentApprovals: () =>
    api.get<StudentApproval[]>("/student-approvals/pending_approvals/"),
  getStudentApprovalStats: () =>
    api.get<StudentApprovalStats>("/student-approvals/approval_stats/"),
  approveStudent: (id: number, notes?: string) =>
    api.post(`/student-approvals/${id}/approve_student/`, { notes }),
  rejectStudent: (id: number, rejection_reason: string, notes?: string) =>
    api.post(`/student-approvals/${id}/reject_student/`, {
      rejection_reason,
      notes,
    }),
  createStudentApproval: (student_id: number, notes?: string) =>
    api.post("/student-approvals/create_approval/", { student_id, notes }),
  getApprovedStudents: () =>
    api.get<ApprovedStudentsResponse>("/student-approvals/approved_students/"),

  // Parent Approval APIs
  getParentApprovalStats: () =>
    api.get<ParentApprovalStats>("/parent-approvals/approval_stats/"),
  getUnapprovedParents: () =>
    api.get<UnapprovedParentsResponse>("/parent-approvals/unapproved_parents/"),
  approveParent: (id: number, notes?: string) =>
    api.post(`/parent-approvals/${id}/approve_parent/`, { notes }),
  rejectParent: (id: number, rejection_reason: string, notes?: string) =>
    api.post(`/parent-approvals/${id}/reject_parent/`, {
      rejection_reason,
      notes,
    }),
  createParentApproval: (parent_id: number, notes?: string) =>
    api.post("/parent-approvals/create_parent_approval/", { parent_id, notes }),

  // Manager Approval Management
  getUnapprovedManagers: () =>
    api.get<UnapprovedManagersResponse>(
      "/manager-approvals/unapproved_managers/",
    ),
  getPendingManagerApprovals: () =>
    api.get<ManagerApproval[]>("/manager-approvals/pending_approvals/"),
  getManagerApprovalStats: () =>
    api.get<ManagerApprovalStats>("/manager-approvals/approval_stats/"),
  approveManager: (id: number, notes?: string) =>
    api.post(`/manager-approvals/${id}/approve_manager/`, { notes }),
  rejectManager: (id: number, rejection_reason: string, notes?: string) =>
    api.post(`/manager-approvals/${id}/reject_manager/`, {
      rejection_reason,
      notes,
    }),
  createManagerApproval: (manager_id: number, notes?: string) =>
    api.post("/manager-approvals/create_approval/", { manager_id, notes }),
  getApprovedManagers: () =>
    api.get<ApprovedManagersResponse>("/manager-approvals/approved_managers/"),

  // Mentor Approval Management (via UserApprovalViewSet)
  approveMentor: (id: number, notes?: string, rating?: number) =>
    api.post(`/user-approvals/${id}/approve_user/`, {
      action: "approve",
      notes,
      rating,
    }),
  rejectMentor: (id: number, rejection_reason: string, notes?: string) =>
    api.post(`/user-approvals/${id}/reject_user/`, {
      action: "reject",
      rejection_reason,
      notes,
    }),

  // Intern Approval Management (via UserApprovalViewSet)
  approveIntern: (id: number, notes?: string) =>
    api.post(`/user-approvals/${id}/approve_user/`, {
      action: "approve",
      notes,
    }),
  rejectIntern: (id: number, rejection_reason: string, notes?: string) =>
    api.post(`/user-approvals/${id}/reject_user/`, {
      action: "reject",
      rejection_reason,
      notes,
    }),

  // Billing & Credits
  getBillingSummary: (cohortId?: number) => {
    const url = cohortId
      ? `/users/billing/?cohort_id=${cohortId}`
      : "/users/billing/";
    return api.get<{
      billing_data: StudentBillingEntry[];
      default_rate_per_credit_usd: number;
      session_duration_minutes: number;
    }>(url);
  },
  getBillingRate: () =>
    api.get<{
      rate_per_credit_usd: number;
      session_duration_minutes: number;
      updated_at: string;
      updated_by: string | null;
    }>("/users/billing-rate/"),
  updateBillingRate: (rate: number) =>
    api.put<{
      rate_per_credit_usd: number;
      session_duration_minutes: number;
      updated_at: string;
      updated_by: string | null;
    }>("/users/billing-rate/", { rate_per_credit_usd: rate }),
  updateStudentBillingRate: (
    studentId: number,
    cohortId: number,
    rate: number,
  ) =>
    api.put<{
      student_id: number;
      cohort_id: number;
      rate_per_credit_usd: number | null;
      effective_rate: number;
      session_duration_minutes: number;
    }>(`/users/${studentId}/update-billing-rate/`, {
      cohort_id: cohortId,
      rate_per_credit_usd: rate,
    }),
  addStudentCredits: (studentId: number, amount: number) =>
    api.post(`/users/${studentId}/add_credits/`, { amount }),
  getOpportunityInterests: () =>
    api.get<OpportunityInterestEntry[]>("/users/opportunity_interests/"),
  sendParentMessage: (
    parentId: number,
    message: string,
    opportunityId?: number,
    cohortId?: number,
  ) =>
    api.post("/users/send_parent_message/", {
      parent_id: parentId,
      message,
      opportunity_id: opportunityId ?? null,
      cohort_id: cohortId ?? null,
    }),
  getStudentAttendanceLogs: (studentId: number, cohortId?: number) => {
    const params = new URLSearchParams();
    if (cohortId) {
      params.append("cohort_id", cohortId.toString());
    }
    const query = params.toString();
    const suffix = query ? `?${query}` : "";
    return api.get<AttendanceRecord[]>(
      `/users/${studentId}/attendance/${suffix}`,
    );
  },

  // Cohort Management APIs
  getCohorts: () => api.get("/cohorts/"),
  getCohort: (id: number) => api.get(`/cohorts/${id}/`),
  getCoursesForCohortDropdown: () =>
    api.get<Array<{ id: number; title: string }>>(
      "/cohorts/courses-for-dropdown/",
    ),
  createCohort: (data: any) => api.post("/cohorts/", data),
  updateCohort: (id: number, data: any) => api.put(`/cohorts/${id}/`, data),
  partialUpdateCohort: (id: number, data: any) =>
    api.patch(`/cohorts/${id}/`, data),
  deleteCohort: (id: number) => api.delete(`/cohorts/${id}/`),
  cloneCohort: (id: number) => api.post<Cohort>(`/cohorts/${id}/clone/`),

  // Cohort Schedule APIs
  getCohortSchedules: (cohortId: number) =>
    api.get(`/cohorts/${cohortId}/schedules/`),
  createCohortSchedule: (cohortId: number, data: any) =>
    api.post(`/cohorts/${cohortId}/schedules/`, data),
  updateCohortSchedule: (cohortId: number, scheduleId: number, data: any) =>
    api.put(`/cohorts/${cohortId}/schedules/${scheduleId}/`, data),
  deleteCohortSchedule: (cohortId: number, scheduleId: number) =>
    api.delete(`/cohorts/${cohortId}/schedules/${scheduleId}/`),
  cloneCohortSchedules: (sourceCohortId: number, targetCohortId: number) =>
    api.post(`/cohorts/${sourceCohortId}/schedules/clone/`, {
      target_cohort_id: targetCohortId,
    }),

  // Tutor Cohort APIs
  getTutorCohorts: () => api.get<Cohort[]>("/cohorts/tutor_cohorts/"),
  getTutorCohortSubmissions: () =>
    api.get<any[]>("/cohorts/tutor_cohort_submissions/"),

  // Mentor Cohort APIs
  getMentorCohorts: () => api.get<Cohort[]>("/cohorts/mentor_cohorts/"),
  getMentorCohortSubmissions: () =>
    api.get<any[]>("/cohorts/mentor_cohort_submissions/"),

  // Mentor Tutor Rating APIs
  getMentorCohortTutors: () =>
    api.get<any[]>("/mentor-tutor-ratings/mentor_cohort_tutors/"),
  rateTutor: (data: any) => api.post("/mentor-tutor-ratings/rate_tutor/", data),
  getMyRatingsReceived: () =>
    api.get<MentorTutorRating[]>("/mentor-tutor-ratings/my_ratings_received/"),

  // Cohort Assignment APIs
  assignMentorToCohort: (cohortId: number, mentorId: number) =>
    api.post(`/cohorts/${cohortId}/admin_assign_mentor/`, {
      mentor_id: mentorId,
    }),
  removeMentorFromCohort: (cohortId: number, mentorId: number) =>
    api.post(`/cohorts/${cohortId}/admin_remove_mentor/`, {
      mentor_id: mentorId,
    }),
  assignTutorToCohort: (cohortId: number, tutorId: number) =>
    api.post(`/cohorts/${cohortId}/admin_assign_tutor/`, { tutor_id: tutorId }),
  removeTutorFromCohort: (cohortId: number, tutorId: number) =>
    api.post(`/cohorts/${cohortId}/admin_remove_tutor/`, { tutor_id: tutorId }),
  // Intern assignment (admin-only) — Phase 4 (issue #1152)
  assignInternToCohort: (cohortId: number, internId: number) =>
    api.post(`/cohorts/${cohortId}/admin_assign_intern/`, {
      intern_id: internId,
    }),
  removeInternFromCohort: (cohortId: number, internId: number) =>
    api.post(`/cohorts/${cohortId}/admin_remove_intern/`, {
      intern_id: internId,
    }),
  assignStudentToCohort: (cohortId: number, studentId: number) =>
    api.post(`/cohorts/${cohortId}/admin_assign_student/`, {
      student_id: studentId,
    }),
  removeStudentFromCohort: (cohortId: number, studentId: number) =>
    api.post(`/cohorts/${cohortId}/admin_remove_student/`, {
      student_id: studentId,
    }),
  bulkAssignCohort: (
    cohortId: number,
    type: "student" | "tutor" | "mentor" | "intern",
    addIds: number[],
    removeIds: number[],
  ) =>
    api.post(`/cohorts/${cohortId}/admin_bulk_assign/`, {
      type,
      add_ids: addIds,
      remove_ids: removeIds,
    }),

  // User Management
  getUserManagement: (filters?: {
    user_type?: string;
    approval_status?: string;
    search?: string;
    page?: number;
    page_size?: number;
  }) => {
    const params = new URLSearchParams();
    if (filters?.user_type) params.append("user_type", filters.user_type);
    if (filters?.approval_status)
      params.append("approval_status", filters.approval_status);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.page_size)
      params.append("page_size", filters.page_size.toString());
    return api.get<UserManagementResponse>(
      `/users/user_management/?${params.toString()}`,
    );
  },

  // Password Reset (Admin only)
  resetUserPassword: (userId: number, password: string) =>
    api.post<{
      message: string;
      user: { id: number; username: string; email: string; user_type: string };
    }>(`/users/${userId}/reset-password/`, { password }),

  // Subscription Management
  getSubscriptions: () => api.get("/subscriptions/"),
  getSubscriptionStats: () => api.get("/subscriptions/subscription_stats/"),
  getExpiringSubscriptions: () => api.get("/subscriptions/expiring_soon/"),
  createSubscription: (data: any) => api.post("/subscriptions/", data),
  updateSubscription: (id: number, data: any) =>
    api.put(`/subscriptions/${id}/`, data),
  activateSubscription: (id: number) =>
    api.post(`/subscriptions/${id}/activate/`),
  cancelSubscription: (id: number) => api.post(`/subscriptions/${id}/cancel/`),
  extendSubscription: (id: number, days: number) =>
    api.post(`/subscriptions/${id}/extend/`, { days }),

  // Parent-Student Assignment (Admin only)
  getParentsList: (search?: string) =>
    api.get<
      Array<{ id: number; username: string; full_name: string; email: string }>
    >(
      `/users/admin_parents_list/${search ? `?search=${encodeURIComponent(search)}` : ""}`,
    ),
  getStudentsList: (search?: string) =>
    api.get<
      Array<{
        id: number;
        username: string;
        full_name: string;
        email: string;
        parent_id: number | null;
        parent_username: string | null;
      }>
    >(
      `/users/admin_students_list/${search ? `?search=${encodeURIComponent(search)}` : ""}`,
    ),
  setStudentParent: (studentId: number, parentId: number | null) =>
    api.post<{ message: string; parent_id?: number; parent_username?: string }>(
      `/users/${studentId}/admin_set_parent/`,
      { parent_id: parentId },
    ),
  setStudentParentFromParent: (
    parentId: number,
    studentId: number,
    action: "add" | "remove",
  ) =>
    api.post<{ message: string }>(
      `/users/${parentId}/admin_set_student_parent/`,
      { student_id: studentId, action },
    ),

  // Generic methods
  get: (url: string) => api.get(url),
  post: (url: string, data: any) => api.post(url, data),
};

// Registration Code API
export const registrationCodeAPI = {
  // Get all registration codes
  getAllCodes: () => api.get<RegistrationCode[]>("/registration-codes/"),

  // Get current week's codes
  getCurrentCodes: () =>
    api.get<RegistrationCode[]>("/registration-codes/current_codes/"),

  // Create new registration code
  createCode: (data: RegistrationCodeCreate) =>
    api.post<RegistrationCode>("/registration-codes/", data),

  // Update registration code
  updateCode: (id: number, data: Partial<RegistrationCodeCreate>) =>
    api.patch<RegistrationCode>(`/registration-codes/${id}/`, data),

  // Delete registration code
  deleteCode: (id: number) => api.delete(`/registration-codes/${id}/`),

  // Generate weekly codes
  generateWeeklyCodes: () =>
    api.post<{ message: string; codes: RegistrationCode[] }>(
      "/registration-codes/generate_weekly_codes/",
    ),

  // Validate registration code (public endpoint)
  validateCode: (data: RegistrationCodeValidation) =>
    api.post<{
      valid: boolean;
      code_type: string;
      week_start: string;
      week_end: string;
    }>("/registration-codes/validate_code/", data),
};

// Course Advertisement API
export interface CourseAdvertisementListResponse {
  results: CourseAdvertisement[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

export const courseAdvertisementAPI = {
  getMyAdvertisements: () =>
    api.get<CourseAdvertisement[]>("/course-advertisements/"),
  getAdvertisements: (params?: {
    approved?: boolean;
    search?: string;
    page?: number;
    page_size?: number;
  }) =>
    api.get<CourseAdvertisementListResponse>("/course-advertisements/", {
      params,
    }),
  getAdvertisement: (id: number) =>
    api.get<CourseAdvertisement>(`/course-advertisements/${id}/`),
  createAdvertisement: (data: CourseAdvertisementCreate) =>
    api.post<CourseAdvertisement>("/course-advertisements/", data),
  updateAdvertisement: (
    id: number,
    data: Partial<CourseAdvertisementCreate> & { is_approved?: boolean },
  ) => api.patch<CourseAdvertisement>(`/course-advertisements/${id}/`, data),
  deleteAdvertisement: (id: number) =>
    api.delete(`/course-advertisements/${id}/`),
  approve: (id: number) =>
    api.post<CourseAdvertisement>(`/course-advertisements/${id}/approve/`),
};

// User Approval API interfaces
export interface UserApproval {
  id: number;
  user: number;
  user_username: string;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  user_type: string;
  status: "pending" | "approved" | "rejected";
  approved_by: number | null;
  approved_by_name: string;
  approval_notes: string | null;
  rejection_reason: string | null;
  approved_at: string | null;
  approval_rating: number | null;
  created_at: string;
  updated_at: string;
}

export interface UserApprovalStats {
  total_users: number;
  pending_approvals: number;
  approved_users: number;
  rejected_users: number;
  approval_rate: number;
}

export interface UserApprovalAction {
  action: "approve" | "reject";
  notes?: string;
  rejection_reason?: string;
  rating?: number;
}

export const userApprovalAPI = {
  // Get all user approvals
  getAllApprovals: () => api.get<UserApproval[]>("/user-approvals/"),

  // Get approval statistics
  getApprovalStats: () =>
    api.get<UserApprovalStats>("/user-approvals/approval_stats/"),

  // Get pending approvals
  getPendingApprovals: () =>
    api.get<UserApproval[]>("/user-approvals/pending_approvals/"),

  // Approve user
  approveUser: (id: number, data: UserApprovalAction) =>
    api.post<{ message: string; status: string }>(
      `/user-approvals/${id}/approve_user/`,
      data,
    ),

  // Reject user
  rejectUser: (id: number, data: UserApprovalAction) =>
    api.post<{ message: string; status: string }>(
      `/user-approvals/${id}/reject_user/`,
      data,
    ),

  // Change decision
  changeDecision: (id: number, data: UserApprovalAction) =>
    api.post<{ message: string; status: string }>(
      `/user-approvals/${id}/change_decision/`,
      data,
    ),
};

// User Disable/Enable API
export interface UserDisableResponse {
  message: string;
  is_disabled: boolean;
}

export const userDisableAPI = {
  // Disable user
  disableUser: (id: number) =>
    api.post<UserDisableResponse>(`/users/${id}/disable_user/`),

  // Enable user
  enableUser: (id: number) =>
    api.post<UserDisableResponse>(`/users/${id}/enable_user/`),

  // Delete user (requires username confirmation)
  deleteUser: (id: number, username: string) =>
    api.post<{ message: string }>(`/users/${id}/delete_user/`, { username }),

  // Get disabled users
  getDisabledUsers: () => api.get<User[]>("/users/disabled_users/"),
};

// Referral Code API interfaces
export interface ReferralCode {
  id: number;
  code: string;
  description: string;
  is_active: boolean;
  max_uses: number | null;
  current_uses: number;
  expiry_date: string | null;
  created_by: number;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  is_available: boolean;
}

export interface ReferralCodeCreate {
  code: string;
  description: string;
  is_active: boolean;
  max_uses: number | null;
  expiry_date: string | null;
}

export interface ReferralCodeStats {
  total_referral_codes: number;
  active_referral_codes: number;
  total_uses_across_all_codes: number;
}

export const referralCodeAPI = {
  // Get all referral codes
  getReferralCodes: () => api.get<ReferralCode[]>("/referral-codes/"),

  // Get specific referral code
  getReferralCode: (id: number) =>
    api.get<ReferralCode>(`/referral-codes/${id}/`),

  // Create new referral code
  createReferralCode: (data: ReferralCodeCreate) =>
    api.post<ReferralCode>("/referral-codes/", data),

  // Update referral code
  updateReferralCode: (id: number, data: Partial<ReferralCodeCreate>) =>
    api.put<ReferralCode>(`/referral-codes/${id}/`, data),

  // Partial update referral code
  patchReferralCode: (id: number, data: Partial<ReferralCodeCreate>) =>
    api.patch<ReferralCode>(`/referral-codes/${id}/`, data),

  // Delete referral code
  deleteReferralCode: (id: number) => api.delete(`/referral-codes/${id}/`),

  // Reset usage count
  resetUsage: (id: number) =>
    api.post<{ status: string; current_uses: number }>(
      `/referral-codes/${id}/reset_usage/`,
    ),

  // Get usage statistics
  getUsageStats: () =>
    api.get<ReferralCodeStats>("/referral-codes/usage_stats/"),
};

// Admin Profile API interfaces
export interface AdminProfileData {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  phone_no: string | null;
  user_type: string;
  date_joined: string;
  last_login: string | null;
  is_active: boolean;
  is_disabled: boolean;
  country?: string;
}

export interface AdminProfileUpdate {
  email?: string;
  phone_no?: string;
  first_name?: string;
  last_name?: string;
  country?: string;
}

export interface AdminProfileResponse {
  message: string;
  profile: AdminProfileData;
}

export const adminProfileAPI = {
  // Get admin profile
  getProfile: () => api.get<AdminProfileData>("/users/admin_profile/"),

  // Update admin profile
  updateProfile: (data: AdminProfileUpdate) =>
    api.patch<AdminProfileResponse>("/users/admin_profile/", data),
};

// Admin Notification Settings API interfaces
export interface AdminNotificationSettingsData {
  id: number;
  admin: number;
  admin_username: string;
  admin_email: string;
  admin_phone: string | null;
  email_notifications: boolean;
  email_tutor_registrations: boolean;
  email_mentor_registrations: boolean;
  email_parent_registrations: boolean;
  email_student_registrations: boolean;
  sms_notifications: boolean;
  sms_tutor_registrations: boolean;
  sms_mentor_registrations: boolean;
  sms_parent_registrations: boolean;
  sms_student_registrations: boolean;
  notification_frequency: "immediate" | "hourly" | "daily" | "weekly";
  created_at: string;
  updated_at: string;
}

export interface AdminNotificationSettingsUpdate {
  email_notifications?: boolean;
  email_tutor_registrations?: boolean;
  email_mentor_registrations?: boolean;
  email_parent_registrations?: boolean;
  email_student_registrations?: boolean;
  sms_notifications?: boolean;
  sms_tutor_registrations?: boolean;
  sms_mentor_registrations?: boolean;
  sms_parent_registrations?: boolean;
  sms_student_registrations?: boolean;
  notification_frequency?: "immediate" | "hourly" | "daily" | "weekly";
}

export const adminNotificationAPI = {
  // Get notification settings
  getSettings: () =>
    api.get<AdminNotificationSettingsData>("/users/notification_settings/"),

  // Create notification settings
  createSettings: (data: AdminNotificationSettingsUpdate) =>
    api.post<AdminNotificationSettingsData>(
      "/users/notification_settings/",
      data,
    ),

  // Update notification settings
  updateSettings: (data: AdminNotificationSettingsUpdate) =>
    api.patch<AdminNotificationSettingsData>(
      "/users/notification_settings/",
      data,
    ),

  // Send test email (admin only)
  sendTestEmail: (email: string) =>
    api.post<{ success: boolean; message: string; count: number }>(
      "/users/test_email/",
      { email },
    ),
};

// Site Settings API (admin only)
export interface SiteSettingsData {
  email_verification_required: boolean;
  marketplace_enabled: boolean;
}

export const siteSettingsAPI = {
  getSettings: () => api.get<SiteSettingsData>("/users/site-settings/"),
  updateSettings: (data: Partial<SiteSettingsData>) =>
    api.patch<SiteSettingsData>("/users/site-settings/", data),
};

// Public site config (no auth; safe flags only — issue #1400)
export interface PublicSiteConfig {
  marketplace_enabled: boolean;
}

export const publicSiteConfigAPI = {
  getConfig: () => api.get<PublicSiteConfig>("/public-site-config/"),
};

// Template API
export const templatesAPI = {
  // Get all templates
  getAll: () => api.get<ProjectTemplate[]>("/templates/"),

  // Get template by ID
  getById: (id: number) => api.get<ProjectTemplate>(`/templates/${id}/`),

  // Create template
  create: (data: Partial<ProjectTemplate>) =>
    api.post<ProjectTemplate>("/templates/", data),

  // Update template
  update: (id: number, data: Partial<ProjectTemplate>) =>
    api.put<ProjectTemplate>(`/templates/${id}/`, data),

  // Partial update template
  patch: (id: number, data: Partial<ProjectTemplate>) =>
    api.patch<ProjectTemplate>(`/templates/${id}/`, data),

  // Delete template
  delete: (id: number) => api.delete(`/templates/${id}/`),

  // Preview question form
  previewQuestionForm: (id: number) =>
    api.get(`/templates/${id}/preview_question_form/`),

  // Preview rating system
  previewRatingSystem: (id: number) =>
    api.get(`/templates/${id}/preview_rating_system/`),

  // Assign template to cohort
  assignToCohort: (id: number, cohortId: number) =>
    api.post(`/templates/${id}/assign_to_cohort/`, { cohort_id: cohortId }),

  // Get available templates for assignment
  getAvailableForAssignment: () =>
    api.get<ProjectTemplate[]>("/templates/available_for_assignment/"),
};

// Blog post view count API (public, no auth required)
export const blogViewsAPI = {
  recordView: (slug: string) =>
    api.post<{ slug: string; view_count: number }>("/blog-views/record/", {
      slug,
    }),
  getCount: (slug: string) =>
    api.get<{ slug: string; view_count: number }>(`/blog-views/${slug}/`),
};

// Registrations API (public, no auth required for list/detail/register)
export const registrationsAPI = {
  list: () => api.get<Registration[]>("/registrations/"),
  getBySlug: (slug: string) => api.get<Registration>(`/registrations/${slug}/`),
  register: (slug: string, data: SignupPayload) =>
    api.post<SignupResponse>(`/registrations/${slug}/register/`, data),
  verifyPayment: (sessionId: string) =>
    api.post<User & { contest_name?: string }>(
      "/registrations/verify-payment/",
      { session_id: sessionId },
    ),
  getCourses: (slug: string, deliveryMode?: string) =>
    api.get<RegistrationCoursesResponse>(
      `/registrations/${slug}/courses/${deliveryMode ? `?delivery_mode=${deliveryMode}` : ""}`,
    ),
  getCourseCohorts: (
    slug: string,
    courseId: number,
    deliveryMode: "online_only" | "in_person" | "hybrid",
  ) =>
    api.get<RegistrationCourseCohortsResponse>(
      `/registrations/${slug}/courses/${courseId}/cohorts/?delivery_mode=${deliveryMode}`,
    ),
};

// Registration Registrations Admin API
export const signupsAPI = {
  list: (params?: Record<string, string>) =>
    api.get<SignupEntry[]>("/signups/", { params }),
  get: (id: number) => api.get<SignupEntry>(`/signups/${id}/`),
  stats: (params?: Record<string, string>) =>
    api.get<RegistrationStats>("/signups/stats/", { params }),
  exportCsvUrl: (params?: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return `${API_BASE_URL}/signups/export_csv/${query ? "?" + query : ""}`;
  },
};

// Admin Registration Management API
export const adminRegistrationsAPI = {
  list: () => api.get<Registration[]>("/admin/registrations/"),
  get: (id: number) => api.get<Registration>(`/admin/registrations/${id}/`),
  create: (data: Partial<Registration>) =>
    api.post<Registration>("/admin/registrations/", data),
  update: (id: number, data: Partial<Registration>) =>
    api.put<Registration>(`/admin/registrations/${id}/`, data),
  patch: (id: number, data: Partial<Registration>) =>
    api.patch<Registration>(`/admin/registrations/${id}/`, data),
  delete: (id: number) => api.delete(`/admin/registrations/${id}/`),
  clone: (id: number) =>
    api.post<Registration>(`/admin/registrations/${id}/clone/`),
  opportunitiesList: () =>
    api.get<Array<{ id: number; title: string }>>(
      "/admin/registrations/opportunities-list/",
    ),
  cohortsList: () =>
    api.get<Array<{ id: number; name: string }>>(
      "/admin/registrations/cohorts-list/",
    ),
};

// Curriculum Management API — GET helpers (tree, completion); provisioning uses `assignmentDocumentProvisioningAPI`.
export const curriculumAPI = {
  getCurriculumTree: (params: { cohort_id: number; curriculum_id: number }) =>
    api.get("/curriculum-assignments/curriculum-tree/", { params }),

  /**
   * Per unit → document: cohort-wide status for assign UI.
   * complete = all students’ document assignments are state complete;
   * assigned = at least one assigned, not all complete; none = no rows.
   */
  getCohortDocumentCompletion: (params: {
    cohort_id: number;
    curriculum_id: number;
  }) =>
    api.get<{
      documents: Record<
        string,
        Record<string, "complete" | "assigned" | "none">
      >;
    }>("/curriculum-assignments/cohort-document-completion/", { params }),

  /**
   * Per unit → document: one student's document assignment status (Manage Assignments, single student).
   * Same shape as cohort completion; scoped to the given student_id.
   */
  getStudentDocumentStatus: (params: {
    cohort_id: number;
    curriculum_id: number;
    student_id: number;
  }) =>
    api.get<{
      documents: Record<
        string,
        Record<string, "complete" | "assigned" | "none">
      >;
    }>("/curriculum-assignments/student-document-status/", { params }),

  listUnitAssignments: (params?: {
    cohort?: number;
    student?: number;
    topic?: number;
    curriculum?: number;
    state?: string;
  }) => api.get("/curriculum-assignments/", { params }),

  // Units (topic-units endpoint)
  getTopicUnits: (subCategoryId: number, state?: string) =>
    api.get("/challenges/topic-units/", {
      params: { sub_category: subCategoryId, state },
    }),

  // Unit state transitions
  submitForReview: (unitId: number) =>
    api.post(`/challenges/${unitId}/submit_review/`),

  approveUnit: (unitId: number) => api.post(`/challenges/${unitId}/approve/`),

  rejectUnit: (unitId: number, rejectionReason: string) =>
    api.post(`/challenges/${unitId}/reject/`, {
      rejection_reason: rejectionReason,
    }),

  archiveUnit: (unitId: number) =>
    api.post(`/challenges/${unitId}/archive_unit/`),

  restoreUnit: (unitId: number) =>
    api.post(`/challenges/${unitId}/restore_unit/`),

  // Dashboard
  getStudentLoad: () =>
    api.get("/dashboard/curriculum/", { params: { view: "student-load" } }),

  getParentDashboard: (childId?: number) =>
    api.get("/dashboard/curriculum/", {
      params: { view: "parent", child_id: childId },
    }),

  // Admin Config
  getAdminConfig: () => api.get("/admin/config/"),

  updateAdminConfig: (key: string, value: string) =>
    api.patch(`/admin/config/${key}/`, { value }),
};

export default api;
